import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import PageTitleBreadcrumb, { breadcrumbHeadingName } from "_shared/_views/breadcrumbs/page-title-breadcrumb";
import { TOUCH_TARGET, touchTargetQuery } from "_shared/_views/touch-target";
import PermissionsSearch from "permissions/_views/search/permissions-search";
import useSessionUser from "permissions/session/use-session-user";
import type { ReactNode } from "react";

/**
 * The top of every permissions page: the way back, what the page is about, and what can be done to it.
 *
 * One scaffold for the person, record, home and templates pages (`docs/design/permissions/
 * PERMISSIONS_UI_DESIGN.md`, "How it looks"): a 48 px avatar or type icon, the title with the shared
 * `PageTitleBreadcrumb` trail in front of it — the crumb is the page's only back control (#5907) — a
 * secondary line, a role chip, and the page's actions, the main one contained and the rest outlined
 * beside it. Below the header sits whatever summary the page has.
 *
 * The title is the page's `h1`, named "Permissions > Dana Ruiz" — the trail without its crumbs'
 * "Back to" (#6324) — so it reads as one heading and the trail does not repeat the name a second
 * time beside the avatar.
 *
 * Above it all sits the permissions search, so the subject can be changed from any permissions page
 * without going home. It is drawn only for a reader with Member Permissions: the people it finds and
 * the pages it opens are theirs alone, and a member reading their own page has nowhere to go with it.
 */

export type PermissionsCrumb = Readonly<{ label: string; to: string }>;

const Header = styled("header")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "auto minmax(0, 1fr)",
  gridTemplateAreas: `
    "search search"
    "avatar title"
    "actions actions"
    "summary summary"
  `,
  alignItems: "center",
  columnGap: theme.spacing(2),
  rowGap: theme.spacing(1.5),
  // The title keeps the whole first line, so a long name is never squeezed by the buttons; the
  // actions sit at the end of the summary's line instead.
  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: "auto minmax(0, 1fr) auto",
    gridTemplateAreas: `
      "search search search"
      "avatar title title"
      "summary summary actions"
    `,
  },
}));

const Avatar = styled(Box)({
  gridArea: "avatar",
  alignSelf: "start",
  "& .MuiAvatar-root": { width: 48, height: 48 },
});

const Title = styled(Box)({ gridArea: "title", minWidth: 0 });

/**
 * The whole width on a phone; at md and up a box of reading width at the end of its line.
 *
 * Under a finger the search's Open and Clear buttons are the fingertip floor, and the input leaves
 * room for both at that size, so typed text never runs under them.
 */
const Search = styled(Box)(({ theme }) => ({
  gridArea: "search",
  minWidth: 0,
  [theme.breakpoints.up("md")]: { justifySelf: "end", width: "100%", maxWidth: "32rem" },
  [touchTargetQuery(theme)]: {
    "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": { minWidth: TOUCH_TARGET, minHeight: TOUCH_TARGET },
    // As specific as MUI's own padding rules for the two indicators, so these win over them.
    "& .MuiAutocomplete-root.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root.MuiAutocomplete-inputRoot, & .MuiAutocomplete-root.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root.MuiAutocomplete-inputRoot":
      { paddingRight: TOUCH_TARGET + 9 },
    "& .MuiAutocomplete-root.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root.MuiAutocomplete-inputRoot": {
      paddingRight: 2 * TOUCH_TARGET + 9,
    },
  },
}));

/**
 * Below md the actions fill the width, two or three to a line, so no button on a phone is a sliver
 * and none wraps its own words; the menu keeps its own size. At md and up they sit at the end of the
 * summary's line. Under a finger every button is at least the fingertip floor tall.
 */
const Actions = styled(Box)(({ theme }) => ({
  gridArea: "actions",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  "& > .MuiButton-root, & > .MuiBox-root:has(> .MuiButton-root)": { flex: "1 1 auto", whiteSpace: "nowrap" },
  [theme.breakpoints.up("md")]: {
    justifyContent: "flex-end",
    "& > .MuiButton-root, & > .MuiBox-root:has(> .MuiButton-root)": { flex: "0 0 auto" },
  },
  "&:empty": { display: "none" },
  [touchTargetQuery(theme)]: { "& .MuiButton-root": { minHeight: TOUCH_TARGET } },
}));

const Summary = styled(Box)({ gridArea: "summary", minWidth: 0, "&:empty": { display: "none" } });

export default function PermissionsSubjectHeader(
  props: Readonly<{
    /** The crumbs before the title, "Permissions" first. */
    trail: readonly PermissionsCrumb[];
    title: string;
    /** Skeleton text stands for the title while what it names is still being looked up. */
    titleLoading?: boolean;
    /** Beside the title, outside the heading: the Retry of a subject whose lookup failed. */
    titleAction?: ReactNode;
    avatar?: ReactNode;
    /** "dana@example.com · Acme LLP". */
    secondary?: ReactNode;
    role?: string;
    /** The page's actions, the main one contained. A reader who may change nothing has only Export CSV. */
    actions?: ReactNode;
    /** The summary strip, the filters, or whatever else belongs to the header. */
    children?: ReactNode;
    /** Puts the cursor in the search when the page opens, as the home page does. */
    focusSearch?: boolean;
  }>,
) {
  const { trail, title, titleLoading, titleAction, avatar, secondary, role, actions, children, focusSearch } = props;
  const sessionUser = useSessionUser();

  return (
    <Header>
      {sessionUser?.canReadMemberPermissions() && (
        <Search>
          <PermissionsSearch focusOnOpen={focusSearch} />
        </Search>
      )}
      {avatar && <Avatar>{avatar}</Avatar>}
      <Title sx={avatar ? undefined : { gridColumn: "1 / span 2" }}>
        <Typography
          component="h1"
          variant="h5"
          aria-label={breadcrumbHeadingName(
            trail.map((crumb) => crumb.label),
            title,
          )}
          sx={{ overflowWrap: "anywhere" }}
        >
          {trail.map((crumb) => (
            <PageTitleBreadcrumb key={crumb.to} label={crumb.label} to={crumb.to} />
          ))}
          {titleLoading ? <Skeleton variant="text" width="12rem" sx={{ display: "inline-block" }} aria-hidden /> : title}
        </Typography>
        {titleAction}
        {(secondary || role) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 1, rowGap: 0.5, mt: 0.25 }}>
            {secondary && (
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                {secondary}
              </Typography>
            )}
            {role && <Chip label={role} size="small" variant="outlined" />}
          </Box>
        )}
      </Title>
      <Actions>{actions}</Actions>
      <Summary>{children}</Summary>
    </Header>
  );
}
